import { useRef, useEffect } from 'react'
import * as THREE from 'three'
import { STAGES } from '../../constants/data'
/**
 * PipelineScene — Three.js CI/CD pipeline visualisation.
 * A glowing signal travels along a CatmullRomCurve3 through 6 stage nodes.
 * Nodes light cyan (running) then lime (passed).
 * Three phases: run → hold → reset (loops forever).
 * Communicates progress to the parent via the onProgress callback.
 */
export function PipelineScene({ onProgress }) {
  const ref = useRef()
  useEffect(() => {
    const canvas = ref.current
    const R = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true })
    R.setPixelRatio(Math.min(devicePixelRatio, 2))
    const S = new THREE.Scene()
    const C = new THREE.PerspectiveCamera(50, 1, 0.1, 100); C.position.set(0, 0, 13.5)
    const resize = () => { const w = canvas.clientWidth || 800, h = canvas.clientHeight || 240; R.setSize(w, h, false); C.aspect = w / h; C.updateProjectionMatrix() }
    resize(); window.addEventListener('resize', resize)

    const n = STAGES.length, spacing = 2.85
    const pts = Array.from({ length: n }, (_, i) => new THREE.Vector3((i - (n - 1) / 2) * spacing, Math.sin(i * 1.4) * 0.85, Math.cos(i * 0.9) * 0.55))
    const curve = new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.25)
    const div   = 240, cpArr = curve.getPoints(div)

    S.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(cpArr), new THREE.LineBasicMaterial({ color: 0x2a3050, transparent: true, opacity: .45 })))
    const pGeo = new THREE.BufferGeometry().setFromPoints(cpArr); pGeo.setDrawRange(0, 0)
    const prog = new THREE.Line(pGeo, new THREE.LineBasicMaterial({ color: 0xc8ff5c, transparent: true, opacity: .95 })); S.add(prog)

    const nodes = pts.map(p => {
      const mesh = new THREE.Mesh(new THREE.SphereGeometry(.4, 18, 18), new THREE.MeshBasicMaterial({ color: 0x3a4060, transparent: true, opacity: .92 })); mesh.position.copy(p); S.add(mesh)
      const halo = new THREE.Mesh(new THREE.SphereGeometry(.4, 18, 18), new THREE.MeshBasicMaterial({ color: 0xc8ff5c, transparent: true, opacity: 0 }));  halo.scale.setScalar(1.9); halo.position.copy(p); S.add(halo)
      return { mesh, halo }
    })
    const pulse      = new THREE.Mesh(new THREE.SphereGeometry(.15, 14, 14), new THREE.MeshBasicMaterial({ color: 0xffffff })); S.add(pulse)
    const pulseHalo  = new THREE.Mesh(new THREE.SphereGeometry(.15, 14, 14), new THREE.MeshBasicMaterial({ color: 0x00d4ff, transparent: true, opacity: .5 })); pulseHalo.scale.setScalar(2.3); S.add(pulseHalo)

    let aid, t = 0, phase = 'run', holdT = 0, lastStage = -1
    const speed = 1 / 6.5
    const animate = () => {
      aid = requestAnimationFrame(animate); const dt = 1 / 60, now = performance.now()
      if      (phase === 'run')  { t += dt * speed; if (t >= 1) { t = 1; phase = 'hold'; holdT = 0 } }
      else if (phase === 'hold') { holdT += dt; if (holdT > 1.3) phase = 'reset' }
      else if (phase === 'reset') { t = 0; phase = 'run'; lastStage = -1; nodes.forEach(nd => { nd.mesh.material.color.setHex(0x3a4060); nd.halo.material.opacity = 0 }); pGeo.setDrawRange(0, 0); onProgress && onProgress(-1, false) }
      pGeo.setDrawRange(0, Math.min(div, Math.floor(t * div)) + 1)
      const p = curve.getPointAt(Math.min(t, .9999)); pulse.position.copy(p); pulseHalo.position.copy(p)
      pulseHalo.material.opacity = .32 + ((Math.sin(now * .01) + 1) / 2) * .28
      const stageIdx = Math.min(n - 1, Math.round(t * (n - 1)))
      if (stageIdx !== lastStage) {
        lastStage = stageIdx
        nodes.forEach((nd, i) => { if (i < stageIdx) { nd.mesh.material.color.setHex(0xc8ff5c); nd.halo.material.opacity = .16 } else if (i === stageIdx) { nd.mesh.material.color.setHex(0x00d4ff); nd.halo.material.opacity = .28 } else { nd.mesh.material.color.setHex(0x3a4060); nd.halo.material.opacity = 0 } })
        onProgress && onProgress(stageIdx, false)
      }
      if (phase === 'hold' && stageIdx === n - 1) { nodes[n - 1].mesh.material.color.setHex(0xc8ff5c); nodes[n - 1].halo.material.opacity = .24 + ((Math.sin(now * .008) + 1) / 2) * .16; if (holdT > .15 && holdT < .2) onProgress && onProgress(stageIdx, true) }
      C.position.x = Math.sin(now * .00018) * .55; C.lookAt(0, 0, 0); R.render(S, C)
    }
    animate()
    return () => { cancelAnimationFrame(aid); window.removeEventListener('resize', resize); R.dispose() }
  }, [])
  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />
}
